import { OwaDate } from '../../../schema';
import getLunarCalendarYearData from './getLunarCalendarYearData';
import { getYear, getMonth, getDate } from '../../../owaDate/getFields';
import type { LunarCalendarType } from './LunarCalendarType';

export type LunarCalendarDate = {
    month: number;
    day: number;
    isLeapMonth: boolean;
};

export default function convertToLunarCalendarDate(
    date: OwaDate,
    lunarCalendarType: LunarCalendarType
): LunarCalendarDate | undefined {
    let year = getYear(date);

    let lunarCalendarYearData = getLunarCalendarYearData(year, lunarCalendarType);

    if (!lunarCalendarYearData) {
        return undefined;
    }

    if (
        isBefore(
            getMonth(date),
            getDate(date),
            lunarCalendarYearData.gregorianMonthOfLunarNewYear,
            lunarCalendarYearData.gregorianDayOfLunarNewYear
        )
    ) {
        // This date is before the lunar new year, so get the data from the previous year
        lunarCalendarYearData = getLunarCalendarYearData(--year, lunarCalendarType);

        if (!lunarCalendarYearData) {
            return undefined;
        }
    }

    const dayInLunarYear = differenceInDays(
        getYear(date),
        getMonth(date),
        getDate(date),
        year,
        lunarCalendarYearData.gregorianMonthOfLunarNewYear,
        lunarCalendarYearData.gregorianDayOfLunarNewYear
    );

    let day = dayInLunarYear;
    let month = 0;

    while (day >= lunarCalendarYearData.numberOfDaysInMonth[month]) {
        day -= lunarCalendarYearData.numberOfDaysInMonth[month];
        month++;
    }

    let isLeapMonth = false;

    if (lunarCalendarYearData.leapMonth) {
        // Numerically there are still only 12 month values,
        // it's just the the second month has an additional tag
        // of leap month.  To use an example from the Gregorian
        // calendar, if the 5th month was the leap month, the
        // months would be: Jan, Feb, March, April, Leap April,
        // May.
        if (month >= lunarCalendarYearData.leapMonth) {
            if (month == lunarCalendarYearData.leapMonth) {
                isLeapMonth = true;
            }

            month--;
        }
    }

    return {
        month: month,
        day: day + 1,
        isLeapMonth: isLeapMonth,
    };
}

function isBefore(month1: number, day1: number, month2: number, day2: number) {
    return month1 < month2 || (month1 == month2 && day1 < day2);
}

function isLeapYear(year: number): boolean {
    return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function differenceInDays(
    year1: number,
    month1: number,
    day1: number,
    year2: number,
    month2: number,
    day2: number
) {
    if (year1 == year2) {
        return (
            getDayOfGregorianYear(year1, month1, day1) - getDayOfGregorianYear(year1, month2, day2)
        );
    } else if (year1 > year2) {
        const dayOfGregorianYear1 = getDayOfGregorianYear(year1, month1, day1);
        const dayOfGregorianYear2 = getDayOfGregorianYear(year2, month2, day2);

        const daysUntilEndOfYear = (isLeapYear(year2) ? 366 : 365) - dayOfGregorianYear2;
        return daysUntilEndOfYear + dayOfGregorianYear1;
    }

    throw new Error('Second date is before first date!');
}

function getDayOfGregorianYear(year: number, month: number, day: number): number {
    const daysSoFarInYear = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
    const daysSoFarInLeapYear = [0, 31, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335];

    let dayOfGregorianYear = 0;

    if (isLeapYear(year)) {
        dayOfGregorianYear = daysSoFarInLeapYear[month];
    } else {
        dayOfGregorianYear = daysSoFarInYear[month];
    }

    return dayOfGregorianYear + day;
}
